import { call, put, takeLatest, select } from 'redux-saga/effects';
import type { PayloadAction } from '@reduxjs/toolkit';
import { api } from '../../services/api';
import type { ApiResponse } from '../../services/api';
import {
  searchSuccess,
  searchFailure,
  setLoading,
} from '../slices/providerSearchSlice';
import type { SearchContext } from '../slices/providerSearchSlice';
import type { Vendor, ProviderSearchFormData, PropertyVendor } from '../../types/providerSearch';
import type { RootState } from '../store';
import { ZIP_COORDINATES } from '../../data/zipCoordinates';

// Worker saga: will be fired on SEARCH_REQUEST actions
export function* fetchVendorsSaga(action: PayloadAction<Record<string, string>>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    // Get search criteria from action payload
    const searchCriteria = action.payload;

    // Call API
    const isExternalUser: boolean = yield select((state: RootState) => state.header.userRole === 'external');
    const response: ApiResponse<(Vendor | PropertyVendor)[]> = yield call(api.searchVendors, searchCriteria, isExternalUser);

    if (response.success) {
      // Process search results based on search type
      const processedResults = response.data;
      // Dispatch success action with processed results and pagination metadata
      const paginationData: any = {
        results: processedResults as (Vendor | PropertyVendor)[]
      };

      // Add pagination metadata if available from API response
      if ('totalCount' in response) {
        paginationData.totalCount = (response as any).totalCount;
        paginationData.currentPage = (response as any).currentPage;
        paginationData.pageSize = (response as any).pageSize;
        paginationData.totalPages = (response as any).totalPages;
      }

      yield put(searchSuccess(paginationData));
    } else {
      yield put(searchFailure(response.message || 'Search failed'));
    }
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'An error occurred';
    yield put(searchFailure(errorMessage));
  } finally {
    yield put(setLoading(false));
  }
}


// Watcher saga: watches for actions and calls the appropriate worker saga
export function* providerSearchSaga(): Generator<any, void, any> {
  yield takeLatest('providerSearch/searchRequest', fetchVendorsSaga)
}
